import type { FC, ReactNode } from 'react'
import type { GlassId } from '@/lib/configurator/options'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { FAQ_ITEMS, PROCESS_STEPS } from '@/constants/content'
import { findGlass } from '@/lib/configurator/options'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: string
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({ eyebrow, title, description, align = 'left', tone = 'light', className }): ReactNode => (
    <Reveal className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
        <p className={cn('eyebrow', tone === 'dark' && '[--eyebrow:var(--brass)]')}>{eyebrow}</p>
        <h2 className={cn('mt-4 font-display text-4xl leading-[1.08] sm:text-5xl', tone === 'dark' ? 'text-ink-foreground' : 'text-foreground')}>{title}</h2>
        {description && <p className={cn('mt-5 text-base leading-relaxed', tone === 'dark' ? 'text-ink-foreground/70' : 'text-muted-foreground')}>{description}</p>}
    </Reveal>
)

export const GlassTile: FC<{ glass: GlassId, className?: string }> = ({ glass, className }): ReactNode => {
    const option = findGlass(glass)

    return (
        <span className={cn('relative block overflow-hidden', className)} style={{ backgroundColor: option.swatch }} aria-hidden='true'>
            {glass === 'mat' && <span className='absolute inset-0' style={{ background: 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.75), rgba(255,255,255,0.25) 70%)' }} />}

            {glass === 'op-aanvraag' && (
                <span className='absolute inset-0 grid grid-cols-3'>
                    <span className='bg-[#5f6366]' />
                    <span className='bg-[#9a7a56]' />
                    <span style={{ backgroundColor: '#d9e4e1', backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 3px, rgba(0,0,0,0.07) 3px 7px)' }} />
                </span>
            )}

            {glass !== 'mat' && (
                <>
                    <span className='absolute -top-1/4 left-[18%] h-[150%] w-[16%] rotate-[22deg] bg-white/35' />
                    <span className='absolute -top-1/4 left-[42%] h-[150%] w-[5%] rotate-[22deg] bg-white/25' />
                </>
            )}
        </span>
    )
}

export const ProcessList: FC<{ tone?: 'light' | 'dark' }> = ({ tone = 'light' }): ReactNode => (
    <ol className='grid gap-px overflow-hidden rounded-md sm:grid-cols-2 lg:grid-cols-3'>
        {PROCESS_STEPS.map((step, index) => (
            <Reveal
                key={step.title}
                as='li'
                delay={index * 70}
                className={cn('flex flex-col gap-4 p-7', tone === 'dark' ? 'bg-white/[0.04] ring-1 ring-white/10' : 'bg-card ring-1 ring-border')}
            >
                <span className={cn('font-display text-5xl leading-none', tone === 'dark' ? 'text-brass' : 'text-brass-strong')}>
                    {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className={cn('text-lg font-semibold', tone === 'dark' && 'text-ink-foreground')}>{step.title}</h3>
                <p className={cn('text-sm leading-relaxed', tone === 'dark' ? 'text-ink-foreground/65' : 'text-muted-foreground')}>{step.text}</p>
            </Reveal>
        ))}
    </ol>
)

export const FaqList: FC<{ limit?: number }> = ({ limit }): ReactNode => {
    const items = limit ? FAQ_ITEMS.slice(0, limit) : FAQ_ITEMS

    return (
        <Accordion className='border-y border-border'>
            {items.map(item => (
                <AccordionItem key={item.question} value={item.question} className='border-border'>
                    <AccordionTrigger className='py-5 text-base font-semibold hover:no-underline'>{item.question}</AccordionTrigger>
                    <AccordionContent className='pb-6 text-[0.95rem] leading-relaxed text-muted-foreground'>{item.answer}</AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    )
}

export const CtaBand: FC<{ title?: string, text?: string }> = ({ title = 'Klaar om uw deur te ontwerpen?', text = 'Stel uw deur samen in 3D en zie direct de prijs per vierkante meter. Bewaar uw ontwerp, vraag een offerte aan of bestel meteen.' }): ReactNode => (
    <section id='ontwerp-uw-deur' className='relative overflow-hidden border-t border-border bg-ink text-ink-foreground'>
        <div className='steel-grid absolute inset-0 opacity-[0.12]' aria-hidden='true' />
        <div className='pointer-events-none absolute -right-40 -bottom-56 size-[38rem] rounded-full bg-[radial-gradient(circle,color-mix(in_oklch,var(--primary)_22%,transparent),transparent_65%)]' aria-hidden='true' />

        <div className='relative mx-auto flex max-w-[1440px] flex-col items-start gap-8 px-5 py-20 sm:px-8 md:flex-row md:items-end md:justify-between lg:px-12 lg:py-28'>
            <Reveal className='max-w-2xl'>
                <p className='eyebrow [--eyebrow:var(--brass)]'>3D-configurator</p>
                <h2 className='mt-4 font-display text-4xl leading-[1.08] sm:text-6xl'>{title}</h2>
                <p className='mt-5 max-w-xl text-base leading-relaxed text-ink-foreground/70'>{text}</p>
            </Reveal>

            <Reveal delay={120} className='flex flex-col gap-3 sm:flex-row'>
                <Link href='/configurator' className='inline-flex h-13 items-center justify-center gap-2 rounded-sm bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85'>
                    Start de configurator
                    <ArrowRight className='size-4' aria-hidden='true' />
                </Link>
                <Link href='/contact' className='inline-flex h-13 items-center justify-center rounded-sm border border-white/25 px-7 text-sm font-semibold transition-colors hover:bg-white/10'>
                    Persoonlijk advies
                </Link>
            </Reveal>
        </div>
    </section>
)
